import { Text, rawString } from '../../Language/Language';
import { Button } from '@repo/ui/Button';
import { Dialog } from '@repo/ui/Dialog';
import { DialogClose } from '@repo/ui/Dialog/DialogClose';
import { DialogContent } from '@repo/ui/Dialog/DialogContent';
import { DialogHeader } from '@repo/ui/Dialog/DialogHeader';
import { DialogRoot } from '@repo/ui/Dialog/DialogRoot';
import { DialogTitle } from '@repo/ui/Dialog/DialogTitle';
import { DialogTrigger } from '@repo/ui/Dialog/DialogTrigger';
import { IconButton } from '@repo/ui/IconButton';
import { ItemCard } from '../../ItemCard/ItemCard';
import { hasAllResolutionsShipmentScope } from '../../../utils/reasonUtils';
import {
  getItemsInSameShipment,
  getShipmentIndexForItem,
} from '../../../utils/shipmentScopeUtils';
import { useWorkflow } from '../../../workflow/hooks';
import type { ShipmentItemsPreviewProps } from './ShipmentItemsPreview.types';

import s from './ShipmentItemsPreview.module.css';

export const ShipmentItemsPreview = ({
  selectedReason,
  order,
  currentItemId,
  shipmentIndex,
  enableShipmentIndexLookup = true,
}: ShipmentItemsPreviewProps) => {
  const { data } = useWorkflow();

  // Check if the selected reason has all shipment scope resolutions
  if (!selectedReason || !hasAllResolutionsShipmentScope(selectedReason)) {
    return null;
  }

  // Guard against null order
  if (!order) {
    return null;
  }

  // Get the tracking number from the workflow data
  const trackingNumber =
    data?.items?.[currentItemId]?.itemSelection?.trackingNumber;

  // Get all items in the same shipment as the current item
  // Use the shipmentIndex from props if available, otherwise calculate it (if lookup is enabled)
  const effectiveShipmentIndex =
    shipmentIndex !== undefined
      ? shipmentIndex
      : enableShipmentIndexLookup
        ? getShipmentIndexForItem(order, currentItemId, trackingNumber)
        : undefined;
  const shipmentItems = getItemsInSameShipment(
    order,
    currentItemId,
    effectiveShipmentIndex,
    trackingNumber,
  );

  // Get the source item ID for virtual items
  const sourceItemId =
    data?.items?.[currentItemId]?.itemSelection?.sourceItemId;
  const itemIdToFilter = sourceItemId || currentItemId;

  // For preview: filter out the current item (or its source item for virtual items)
  const otherShipmentItems = shipmentItems.filter(
    (item) => item.id !== itemIdToFilter,
  );

  // For popup dialog: include all shipment items (including the original item)
  const allShipmentItems = shipmentItems;

  const itemCount = otherShipmentItems.length;

  if (itemCount === 0) {
    return null;
  }

  return (
    <div className={s.previewContainer}>
      <div className={s.previewHeader}>
        <span className={s.itemCountText}>
          <Text
            langKey="Global.reasons.shipmentPreview.includedInPackage"
            replacementTokens={{ itemCount: itemCount.toString() }}
          />
        </span>
        <DialogRoot>
          <DialogTrigger asChild={true}>
            <Button variant="link" className={s.viewItemsLink} type="button">
              <Text langKey="Global.reasons.shipmentPreview.viewItems" />
            </Button>
          </DialogTrigger>
          <Dialog>
            <DialogHeader className={s.modalHeader}>
              <DialogTitle position="center">
                <Text
                  langKey="Global.reasons.shipmentPreview.dialogTitle"
                  replacementTokens={{
                    itemCount: allShipmentItems.length.toString(),
                  }}
                />
              </DialogTitle>
              <DialogClose asChild={true}>
                <IconButton icon="close" label="Close" />
              </DialogClose>
            </DialogHeader>
            <DialogContent className={s.modalContent}>
              <div className={s.modalItemList}>
                {allShipmentItems.map((item) => (
                  <ItemCard
                    key={item.id}
                    item={item}
                    imageSize="sm"
                    className={s.modalItemCard}
                    containerClassName={s.modalItemCardWrapper}
                    additionalDetails={[
                      {
                        name:
                          rawString({
                            langKey: 'Global.reasons.shipmentPreview.quantity',
                          }) ?? 'Quantity',
                        value: '1',
                      },
                    ]}
                  />
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </DialogRoot>
      </div>
      <div className={s.thumbnailRow}>
        {otherShipmentItems.slice(0, 4).map((item) => (
          <div
            key={item.id}
            className={s.thumbnail}
            data-testid="shipmentItemsPreviewItem"
          >
            {item.image?.url ? (
              <img
                src={item.image.url}
                alt={item.title}
                className={s.thumbnailImage}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div
                className={s.placeholderThumbnail}
                role="img"
                aria-label={
                  rawString({
                    langKey: 'Global.itemSelection.noImage',
                    fallback: 'No image available',
                  }) ?? 'No image available'
                }
              >
                <Text langKey="Global.itemSelection.noImage" />
              </div>
            )}
          </div>
        ))}
        {otherShipmentItems.length > 4 && (
          <div className={s.moreItemsIndicator}>
            +{otherShipmentItems.length - 4}
          </div>
        )}
      </div>
    </div>
  );
};
